import { useEffect, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { ArrowUUpLeft } from "@phosphor-icons/react";

// Reliable, always-available way to move an item into a container / location / top level.
export default function MoveToDialog({ asset, open, onOpenChange, onMoved }) {
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQ("");
    api.get("/assets?scope=all").then((r) => setAssets(r.data || [])).catch(() => {});
    api.get("/config/locations").then((r) => setLocations(r.data.items || r.data || [])).catch(() => {});
  }, [open]);

  const move = async (payload, label) => {
    setBusy(true);
    try {
      await api.post(`/assets/${asset.id}/move`, payload);
      toast.success(`Moved to ${label}`);
      onOpenChange(false);
      onMoved && onMoved();
    } catch (e) { toast.error(e.response?.data?.detail || "Move failed"); }
    setBusy(false);
  };

  const candidates = assets.filter((a) => a.id !== asset?.id &&
    (!q || `${a.asset_number} ${a.name}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto" data-testid="move-to-dialog">
        <DialogHeader><DialogTitle className="font-display">Move {asset?.asset_number} — {asset?.name}</DialogTitle>
          <DialogDescription>Choose a container, a location, or remove it to top level.</DialogDescription></DialogHeader>
        <Button data-testid="move-top-level" disabled={busy} variant="outline"
          onClick={() => move({ to_top_level: true }, "top level")} className="justify-start font-semibold">
          <ArrowUUpLeft size={16} className="mr-1" />Remove from container (top level)
        </Button>
        <div className="text-xs font-bold uppercase text-slate-500 mt-3 mb-1">Into a container, vehicle or bag</div>
        <Input data-testid="move-search" placeholder="Search assets…" value={q} onChange={(e) => setQ(e.target.value)} className="h-9" />
        <div className="flex flex-col gap-1 max-h-56 overflow-y-auto mt-1">
          {candidates.slice(0, 60).map((a) => (
            <button key={a.id} data-testid={`move-dest-${a.id}`} disabled={busy}
              onClick={() => move({ container_asset_id: a.id }, `${a.asset_number} — ${a.name}`)}
              className="text-left text-sm border border-slate-200 rounded px-2 py-1.5 hover:bg-slate-50">
              <span className="font-mono text-xs text-slate-500">{a.asset_number}</span> {a.name}
              {a.child_count ? <span className="text-xs text-slate-400"> · contains {a.child_count}</span> : ""}
            </button>
          ))}
          {candidates.length === 0 && <div className="text-sm text-slate-400 px-1 py-2">No matching assets.</div>}
        </div>
        {locations.length > 0 && <>
          <div className="text-xs font-bold uppercase text-slate-500 mt-3 mb-1">Or a physical location</div>
          <div className="flex flex-wrap gap-1">
            {locations.map((l) => (
              <button key={l.id} data-testid={`move-loc-${l.id}`} disabled={busy}
                onClick={() => move({ to_top_level: true, location_id: l.id }, l.name)}
                className="text-sm border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">{l.name}</button>
            ))}
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}
